import { createContext, useState, useEffect, useRef, useContext, useCallback } from 'react';

// Utility
import { useStripe, useElements, CardNumberElement } from '@stripe/react-stripe-js';
import * as isValid from 'utility/validation';
import {
    postUserAndPayment,
    getDiscountByCode,
    releaseSeatsHoldToken,
    extendSeatsHoldToken,
} from 'utility/requests';
import { getRegistrationQuestions } from 'registration/RegistrationAPI';
import {
    isRegistrationRequired,
    initializeAttendee,
    formatRegistrationFormForPurchase,
} from 'registration/RegistrationUtils';
import { t } from '@lingui/macro';
import { useLingui } from '@lingui/react';
import * as GA4 from 'analytics/ga4';
import * as MetaPixel from 'analytics/metaPixel';
import { calculateOrder } from 'calculations';
import { googleAnalytics } from 'services/googleAnalytics';
import { sourceTracker } from 'services/sourceTracking';
import { buildReceipt } from 'services/receipt';
import { useNotification } from 'utility/notification';
import {
    getCountryProfile,
    normalizeCountryCode,
    normalizePhone,
    normalizePostalCode,
} from 'utility/countries';
import { validateForm } from 'registration/RegistrationUtils';

const CheckoutContext = createContext(null);

export const useCheckoutContext = () => {
    const context = useContext(CheckoutContext);

    if (!context) throw new Error('must wrap with CheckoutProvider');

    return context;
};

function initializeForm(event) {
    const defaultState = {
        firstName: { value: '', error: false, message: '' },
        lastName: { value: '', error: false, message: '' },
        email: { value: '', error: false, message: '' },
        emailConfirm: { value: '', error: false, message: '' },
        address: { value: '', error: false, message: '' },
        city: { value: '', error: false, message: '' },
        province: { value: '', error: false, message: '' },
        country: { value: normalizeCountryCode(event.country), error: false, message: '' },
        postalCode: { value: '', error: false, message: '' },
        ccName: { value: '', error: false, message: '' },
        phone: { value: '', error: false, message: '' },
    };

    const sessionData = JSON.parse(sessionStorage.getItem(process.env.REACT_APP_CHECKOUT_KEY));

    if (sessionData) {
        if (
            sessionData.eventId === event.eventId &&
            Date.now() - sessionData.lastUpdated < 1 * 60 * 60 * 1000
        ) {
            // Restore user checkout session
            Object.keys(defaultState).forEach((p) => {
                defaultState[p].value =
                    p === 'country'
                        ? normalizeCountryCode(sessionData.form[p])
                        : sessionData.form[p];
            });
        } else {
            // Clear invalid checkout form data
            sessionStorage.removeItem(process.env.REACT_APP_CHECKOUT_KEY);
        }
    }

    return defaultState;
}

export const CheckoutProvider = ({ children, ...props }) => {
    const {
        products,
        bundles,
        handleAddProduct,
        handleRemoveProduct,
        handleAddBundle,
        handleRemoveBundle,
        event,
        discount,
        discountFormState,
        discountDispatch,
        onDiscountChange,
        accessCodes,
        onPurchase,
        onCancel,
    } = props;

    const { i18n } = useLingui();
    const stripe = useStripe();
    const elements = useElements();
    const { createNotification } = useNotification();

    // State
    const [status, setStatus] = useState('Loading'); // status = Loading | Error | SeatPicker | Registration | Checkout | Submit
    const [errorMessage, setErrorMessage] = useState('');

    const [seatsPicked, setSeatsPicked] = useState([]);

    // Chart reference for programmatic control (e.g., clearing selections)
    const [chartRef, setChartRef] = useState(null);

    // Key to force SeatsPicker remount when clearing selection
    const [chartKey, setChartKey] = useState(0);

    // Flag to track intentional session clearing (to prevent expiry handler from closing modal)
    const isIntentionalClearRef = useRef(false);

    // Session timer state
    const [sessionExpiresAt, setSessionExpiresAt] = useState(null);
    const [sessionSecondsRemaining, setSessionSecondsRemaining] = useState(null);
    const [holdToken, setHoldToken] = useState(null);
    const [hasShownOneMinuteWarning, setHasShownOneMinuteWarning] = useState(false);
    const hasAddedCheckoutTimeRef = useRef(false);

    const [chartSelectionErrors, setChartSelectionErrors] = useState([]); // Errors

    // Session alert snackbar state
    const [sessionAlert, setSessionAlert] = useState({
        open: false,
        message: '',
        severity: 'warning', // 'warning' | 'error'
    });

    const closeSessionAlert = useCallback(() => {
        setSessionAlert((prev) => ({ ...prev, open: false }));
    }, []);

    // Session expired dialog state (shown when timer reaches 0)
    const [sessionExpiredDialogOpen, setSessionExpiredDialogOpen] = useState(false);

    const handleSessionExpiredDialogClose = () => {
        setSessionExpiredDialogOpen(false);
        onCancel();
    };

    const contentRef = useRef();

    // Registration State
    const registrationEnabled = Boolean(
        event?.checkoutQuestionMode === 1 || event?.checkoutQuestionMode === 2,
    );
    const [registrationQuestions, setRegistrationQuestions] = useState({
        custom: [],
        required: [],
    });
    const [registrationForm, setRegistrationForm] = useState(null);
    const registrationRequired = isRegistrationRequired(
        event.checkoutQuestionMode,
        registrationQuestions.custom,
        registrationQuestions.required,
        products,
        bundles,
        seatsPicked,
    );

    let paidTransaction = false;
    let showDiscountForm = false;

    const receiptData = buildReceipt(products, bundles, event, discount, seatsPicked);

    if (receiptData.total > 0) {
        paidTransaction = true;
        showDiscountForm = true;
    }

    if (receiptData.discountsApplied > 0) {
        showDiscountForm = true;
    }

    // Initialize State
    // If registration is enabled: download and save the questions
    useEffect(() => {
        async function init() {
            if (registrationEnabled) {
                const registrationQuestions = await getRegistrationQuestions(event.eventId);
                setRegistrationQuestions(registrationQuestions);
            }
            setStatus(event.seatsEnabled ? 'SeatPicker' : 'Tickets');
        }
        init().catch((e) => {
            setStatus('Error');
        });
        // eslint-disable-next-line
    }, []);

    // Resets the scroll position of the browser before rendering the registration or payment forms
    useEffect(() => {
        if (status === 'Registration' || status === 'Checkout') {
            contentRef.current.scrollIntoView({ block: 'end' });
        }
    }, [status]);

    // Track if we've already handled the session expiry (to prevent multiple calls)
    const hasHandledExpiryRef = useRef(false);

    // Session countdown timer
    useEffect(() => {
        if (!sessionExpiresAt) {
            hasHandledExpiryRef.current = false;
            return;
        }

        const updateTimer = () => {
            const now = new Date();
            const expiresAt = new Date(sessionExpiresAt);
            const diffMs = expiresAt - now;
            const diffSeconds = Math.max(0, Math.floor(diffMs / 1000));
            setSessionSecondsRemaining(diffSeconds);

            // Show 1 minute warning
            if (diffSeconds <= 60 && diffSeconds > 0 && !hasShownOneMinuteWarning) {
                setHasShownOneMinuteWarning(true);
                setSessionAlert({
                    open: true,
                    message: t`Your session will expire in 1 minute. Please complete your purchase soon.`,
                    severity: 'warning',
                });
            }

            // Handle session expiry when timer reaches 0
            if (diffSeconds === 0 && !hasHandledExpiryRef.current) {
                hasHandledExpiryRef.current = true;
                // Use setTimeout to ensure state updates from setSessionSecondsRemaining are processed first
                setTimeout(() => {
                    handleSessionExpired();
                }, 0);
            }
        };

        // Update immediately
        updateTimer();

        // Then update every second
        const interval = setInterval(updateTimer, 1000);

        return () => clearInterval(interval);
    }, [sessionExpiresAt, hasShownOneMinuteWarning]);

    // Add 8 minutes when user reaches Checkout page (one-time only)
    useEffect(() => {
        if (status === 'Checkout' && holdToken && !hasAddedCheckoutTimeRef.current) {
            hasAddedCheckoutTimeRef.current = true;

            // Extend the hold token on seats.io and update frontend timer
            extendSeatsHoldToken(event.eventId, holdToken, 8)
                .then((result) => {
                    setSessionExpiresAt(result.expiresAt);
                })
                .catch((error) => {
                    console.error('Failed to extend hold token:', error);
                    // Fallback: extend frontend timer only (seats may expire earlier)
                    const currentExpiry = new Date(sessionExpiresAt);
                    const newExpiry = new Date(currentExpiry.getTime() + 8 * 60 * 1000);
                    setSessionExpiresAt(newExpiry.toISOString());
                });
        }
    }, [status, holdToken, event.eventId, sessionExpiresAt]);

    // Warn user before closing/refreshing tab during checkout with active session
    useEffect(() => {
        if (!holdToken) return;

        const handleBeforeUnload = (e) => {
            // Show browser's native "Leave site?" confirmation dialog
            e.preventDefault();
            e.returnValue = '';
            return '';
        };

        const handleUnload = () => {
            // Use sendBeacon to release hold token - it's reliable during page unload
            // Unlike fetch, sendBeacon is guaranteed to be sent even when the page is closing
            // Note: sendBeacon sends as text/plain by default, backend parses it as JSON
            if (holdToken) {
                const url = `${process.env.REACT_APP_API_URL}/store_api/events/${event.eventId}/seats/release-hold`;
                const data = JSON.stringify({ holdToken });
                navigator.sendBeacon(url, data);

                // Also clear local session storage
                Object.keys(sessionStorage).forEach((key) => {
                    if (key.startsWith('seatsio')) {
                        sessionStorage.removeItem(key);
                    }
                });
            }
        };

        window.addEventListener('beforeunload', handleBeforeUnload);
        window.addEventListener('unload', handleUnload);

        return () => {
            window.removeEventListener('beforeunload', handleBeforeUnload);
            window.removeEventListener('unload', handleUnload);
        };
    }, [holdToken, event.eventId]);

    // Handle session initialization from seats.io
    const handleSessionInitialized = (holdTokenData) => {
        setHoldToken(holdTokenData.token);
        setSessionExpiresAt(holdTokenData.expiresAt);
        setSessionSecondsRemaining(holdTokenData.expiresInSeconds);
        setHasShownOneMinuteWarning(false);
    };

    // Handle session expiry - reset all checkout state
    const handleSessionExpired = () => {
        // If this is an intentional clear (user clicked "Clear Selection"), don't show alert or close modal
        if (isIntentionalClearRef.current) {
            isIntentionalClearRef.current = false;
            return;
        }

        // Show session expired dialog
        setSessionExpiredDialogOpen(true);

        // Reset session state
        setHoldToken(null);
        setSessionExpiresAt(null);
        setSessionSecondsRemaining(null);
        setHasShownOneMinuteWarning(false);
        hasAddedCheckoutTimeRef.current = false;

        // Reset seat selections
        setSeatsPicked([]);
    };

    // Release hold token via backend API (expire immediately)
    // Set isIntentionalClear=true when user clicks "Clear Selection" to prevent expiry handler from closing modal
    const releaseHoldToken = async (isIntentionalClear = false) => {
        if (!holdToken && !isIntentionalClear) return;

        // Set flag before releasing so handleSessionExpired knows to skip closing modal
        if (isIntentionalClear) {
            isIntentionalClearRef.current = true;
        }

        try {
            // Call backend API to expire the hold token
            if (holdToken) {
                await releaseSeatsHoldToken(event.eventId, holdToken);
            }
        } catch (error) {
            console.error('Error releasing hold token:', error);
        }

        // Reset session state regardless of API result
        setHoldToken(null);
        setSessionExpiresAt(null);
        setSessionSecondsRemaining(null);
        setHasShownOneMinuteWarning(false);
        hasAddedCheckoutTimeRef.current = false;
        setSeatsPicked([]);

        // Increment chartKey to force SeatsPicker to remount with fresh session
        if (isIntentionalClear) {
            setChartKey((prev) => prev + 1);
        }
    };

    // Clear all seat selections using seats.io API (keeps session active)
    const clearSelection = async () => {
        if (!chartRef) return;

        try {
            // Get all currently selected objects from the chart
            const selectedObjects = await chartRef.listSelectedObjects();

            if (selectedObjects.length > 0) {
                // Deselect all objects - this triggers onObjectDeselected for each
                // which will update seatsPicked state automatically
                const labels = selectedObjects.map((obj) => obj.label);
                await chartRef.deselectObjects(labels);
            }
        } catch (error) {
            console.error('Error clearing selections:', error);
            // Fallback: just clear the state
            setSeatsPicked([]);
        }
    };

    // Deselect a single seat using seats.io API
    const deselectSeat = async (seat) => {
        if (!chartRef) return;

        try {
            // For General Admission areas, we need to specify the ticketType
            // to deselect just one ticket instead of the entire area
            if (seat.objectType === 'GeneralAdmissionArea') {
                await chartRef.deselectObjects([
                    { id: seat.id, ticketType: seat.selectedTicketType },
                ]);
            } else {
                await chartRef.deselectObjects([seat.id]);
            }
        } catch (error) {
            console.error('Error deselecting seat:', error);
            // Fallback: remove from state manually
            if (seat.objectType === 'GeneralAdmissionArea') {
                setSeatsPicked((prev) => {
                    let removed = false;
                    return prev.filter((el) => {
                        if (
                            !removed &&
                            el.uuid === seat.uuid &&
                            el.selectedTicketType === seat.selectedTicketType
                        ) {
                            removed = true;
                            return false;
                        }
                        return true;
                    });
                });
            } else {
                setSeatsPicked((prev) => prev.filter((s) => s.id !== seat.id));
            }
        }
    };

    // Format seconds to MM:SS
    const formatTimer = (seconds) => {
        if (seconds === null) return null;
        const mins = Math.floor(seconds / 60);
        const secs = seconds % 60;
        return `${mins}:${secs.toString().padStart(2, '0')}`;
    };

    const formattedTimer = formatTimer(sessionSecondsRemaining);

    // *** Reset Registration Form when Cart is Updated *** //

    const handleAddProductOverride = (id) => {
        if (registrationForm) {
            setRegistrationForm(null);
        }
        handleAddProduct(id);
    };
    const handleRemoveProductOverride = (id) => {
        if (registrationForm) {
            setRegistrationForm(null);
        }
        handleRemoveProduct(id);
    };
    const handleAddBundleOverride = (id) => {
        if (registrationForm) {
            setRegistrationForm(null);
        }
        handleAddBundle(id);
    };
    const handleRemoveBundleOverride = (id) => {
        if (registrationForm) {
            setRegistrationForm(null);
        }
        handleRemoveBundle(id);
    };

    // The callbacks for adding/removing seats are down in the SeatPicker component. We should consider moving them up here into CheckoutContext.
    // For now, we'll just use an effect to clear the RegistrationForm whenever the seatsPicked array is changed
    useEffect(() => {
        setRegistrationForm(null);
    }, [seatsPicked]);

    // Customer Contact Form
    const [customerForm, setCustomerForm] = useState(() => initializeForm(event));

    // Robin SMS opt-in: starts unchecked per consent decision; the listener on
    // the API side won't fire the webhook unless this is true.
    const [robinOptIn, setRobinOptIn] = useState(() => {
        const sessionData = JSON.parse(sessionStorage.getItem(process.env.REACT_APP_CHECKOUT_KEY));
        if (
            sessionData?.eventId === event.eventId &&
            Date.now() - sessionData.lastUpdated < 1 * 60 * 60 * 1000 &&
            sessionData.robinOptIn != null
        ) {
            return Boolean(sessionData.robinOptIn);
        }
        return false;
    });

    // Backup form values to sessionStorage every time the state changes
    useEffect(() => {
        sessionStorage.setItem(
            process.env.REACT_APP_CHECKOUT_KEY,
            JSON.stringify({
                eventId: event.eventId,
                lastUpdated: Date.now(),
                robinOptIn,
                form: Object.keys(customerForm).reduce((p, n) => {
                    p[n] = customerForm[n].value;
                    return p;
                }, {}),
            }),
        );
    }, [customerForm, event.eventId, robinOptIn]);

    // Credit Card Form
    const [paymentForm, setPaymentForm] = useState({
        ccNumber: { error: false },
        cvc: { error: false },
        ccExpiry: { error: false },
    });

    const handleFormChange = (field, value) => {
        let newState = { ...customerForm };

        switch (field) {
            case 'country':
                const countryCode = normalizeCountryCode(value);
                newState = {
                    ...customerForm,
                    [field]: { ...customerForm[field], value: countryCode },
                    phone: { value: '', error: false, message: '' },
                    province: { value: '', error: false, message: '' },
                    postalCode: { value: '', error: false, message: '' },
                };
                break;

            case 'postalCode':
                const postal = normalizePostalCode(value, customerForm.country.value);
                newState = { ...customerForm, [field]: { ...customerForm[field], value: postal } };
                break;

            case 'phone':
                let phone = normalizePhone(value, customerForm.country.value);
                newState = { ...customerForm, [field]: { ...customerForm[field], value: phone } };
                break;

            case 'email':
            case 'emailConfirm':
                let email = value.replace(/\s/g, '');
                newState = { ...customerForm, [field]: { ...customerForm[field], value: email } };
                break;

            default:
                newState = { ...customerForm, [field]: { ...customerForm[field], value } };
        }

        setCustomerForm(newState);
    };

    const handleAddressAutocomplete = (address, city, province, country, postal) => {
        const countryCode = normalizeCountryCode(country);
        const countryProfile = getCountryProfile(countryCode);

        setCustomerForm((s) => ({
            ...s,
            address: { value: address, error: false, message: '' },
            city: { value: city, error: false, message: '' },
            province: {
                value: countryProfile.region.mode === 'hidden' ? '' : province,
                error: false,
                message: '',
            },
            country: { value: countryCode, error: false, message: '' },
            postalCode: {
                value: normalizePostalCode(postal, countryCode),
                error: false,
                message: '',
            },
        }));

        // TODO: We need to backup the latest form changes to localStorage.
        //       We can do this here, or take the backup logic from handleFormChange() and extract it to an effect that runs on every render
    };

    async function handleFormSubmit() {
        if (discountFormState.status === 'loading') return null;

        if (showDiscountForm && discountFormState.code && discountFormState.status !== 'success') {
            const message = t`Apply your discount code or remove it to continue.`;
            discountDispatch({
                type: 'setStatus',
                status: 'error',
                message,
            });
            setErrorMessage(message);
            document.getElementById('discount-code')?.focus();
            return null;
        }

        if (isFormValid() === false) {
            setErrorMessage(t`Please review required fields`);

            GA4.exception('Payment Error: missing fields');

            return null;
        }
        setStatus('Submit');

        // Form is valid. Processing payment
        try {
            let tokenId = null;

            // Create stripe token if user buys a paid product
            if (paidTransaction) {
                // Create token
                const cardNumberElement = elements.getElement(CardNumberElement);
                const { token, error } = await stripe.createToken(cardNumberElement, {
                    name: customerForm.ccName.value,
                });

                // Return if credit card fields are invalid
                if (error) {
                    switch (error.code) {
                        case 'incomplete_number':
                            setErrorMessage(t`Please review required fields`);
                            setPaymentForm({
                                ccNumber: { error: true },
                                cvc: { error: false },
                                ccExpiry: { error: false },
                            });
                            break;

                        case 'incomplete_cvc':
                            setErrorMessage(t`Please review required fields`);
                            setPaymentForm({
                                ccNumber: { error: false },
                                cvc: { error: true },
                                ccExpiry: { error: false },
                            });
                            break;

                        case 'incomplete_expiry':
                            setErrorMessage(t`Please review required fields`);
                            setPaymentForm({
                                ccNumber: { error: false },
                                cvc: { error: false },
                                ccExpiry: { error: true },
                            });
                            break;

                        case 'card_declined':
                            setErrorMessage(t`Your payment was declined`);
                            break;

                        default:
                            setErrorMessage(t`Payment failed. Please try again.`);
                    }

                    setStatus('Checkout');
                    return null;
                } else {
                    tokenId = token.id;
                    setPaymentForm({
                        ccNumber: { error: false },
                        cvc: { error: false },
                        ccExpiry: { error: false },
                    });
                }
            }

            // *** Pre-Calculate Order *** //
            // API won't process the order if the values passed up do not match

            const cart = bundles.concat(products).map((i) => ({
                name: i.name,
                price: i.price,
                qty: i.qty,
                paidProductCount: i.paidProductCount,
            }));

            // TODO: we run buildReceipt() on every render. We may be able to use the output from there instead of calculating again here with calculateOrder()
            //       look into this later
            const { discountsApplied } = calculateOrder(
                cart,
                products,
                bundles,
                seatsPicked,
                event,
                discount,
            );

            const transactionData = {
                locale: i18n.locale,
                event: { eventId: event.eventId },
                customer: {
                    country: customerForm.country.value,
                    region: customerForm.province.value,
                    email: customerForm.email.value,
                    postal: customerForm.postalCode.value,
                    city: customerForm.city.value,
                    tel: customerForm.phone.value,
                    fn: customerForm.firstName.value,
                    ln: customerForm.lastName.value,
                    sa: customerForm.address.value,
                    sourceApp: 'Storefront',
                },
                products: products
                    .filter((p) => p.qty > 0)
                    .map((p) => ({ id: p.id, quantity: p.qty, price: p.price })),
                bundles: bundles
                    .filter((p) => p.qty > 0)
                    .map((p) => ({ id: p.id, quantity: p.qty, price: p.price })),
                stripe: {
                    source: tokenId,
                    description: event.title,
                    currency: event.currency,
                },
                discountCode: null,
                registration: formatRegistrationFormForPurchase(registrationForm),
                accessCodes: accessCodes,
                clientValues: {
                    ticketsDiscounted: discountsApplied,
                },
                seatsPicked: seatsPicked.map((sp) => ({
                    // *** SeatsIO Fields *** //
                    id: sp.id,
                    objectType: sp.objectType,
                    productId: sp.productId,
                    category: {
                        key: sp.category.key,
                    },
                    labels: {
                        section: sp.labels.section,
                        parent: sp.labels.parent,
                        own: sp.labels.own,
                    },
                    parent: { type: sp.parent?.type },
                    // Tables Only: When user buys a whole table, it has a list of seats that they contain
                    seats: sp.seats?.map((s) => ({
                        // *** SeatsIO Fields *** //
                        id: s.id,
                        objectType: s.objectType,
                        productId: sp.productId, // reuse productId of the parent table
                        labels: {
                            section: s.labels.section,
                            parent: s.labels.parent,
                            own: s.labels.own,
                        },
                        parent: { type: s.parent?.type },
                        // *** Custom Fields *** //
                        section: {
                            type: s.labels.section ? 'section' : null,
                            label: s.labels.section,
                        },
                        row: {
                            type: s.parent?.type?.toLowerCase() || null,
                            label: s.labels.parent,
                        },
                        seat: {
                            type:
                                s.objectType === 'GeneralAdmissionArea'
                                    ? 'ga'
                                    : s.objectType.toLowerCase(), // ga, seat
                            label: s.labels.own,
                        },
                    })),
                    // *** Custom Fields *** //
                    section: {
                        type: sp.labels.section ? 'section' : null,
                        label: sp.labels.section,
                    },
                    row: {
                        type: sp.parent?.type?.toLowerCase() || null,
                        label: sp.labels.parent,
                    },
                    seat: {
                        type:
                            sp.objectType === 'GeneralAdmissionArea'
                                ? 'ga'
                                : sp.objectType.toLowerCase(), // ga, seat
                        label: sp.labels.own,
                    },
                })),
                holdToken: holdToken,
                sourceSlug: sourceTracker.getSourceSlug(),
                robinOptIn: event.robinEnabled && robinOptIn,
            };

            if (discountFormState.status === 'success') {
                transactionData.discountCode = discount.code;
            }

            postUserAndPayment(transactionData)
                .then(async (data) => {
                    if (data.paid === true) {
                        const receiptData = buildReceipt(
                            products,
                            bundles,
                            event,
                            discount,
                            seatsPicked,
                        );

                        discountDispatch({ type: 'deleteCode' }); // Most cleanup is in StorefrontContext, but discountDispatch is not that high up the tree

                        onPurchase(
                            customerForm.email.value,
                            discount,
                            data.ticketCount,
                            data.transaction.id,
                            `${data.appleWalletURL}&lang=${i18n.locale}`,
                            `${data.googleWalletURL}&lang=${i18n.locale}`,
                            data.googleCalendarURL,
                            data.outlookCalendarURL,
                            data.officeCalendarURL,
                            data.calendarURL,
                            receiptData,
                        );

                        try {
                            const { id, total, tax } = data.transaction;
                            const discountCode = discount?.code;
                            const currency = event.currency;

                            // Log purchase in google analytics and meta pixel
                            GA4.purchase(id, total, tax, discountCode, currency);

                            googleAnalytics.purchaseCompleted(
                                id,
                                total,
                                tax,
                                discountCode,
                                [],
                                currency,
                            );

                            if (event.metaPixelId) {
                                MetaPixel.purchase(event.metaPixelId, total, currency);
                            }

                            sessionStorage.removeItem(process.env.REACT_APP_REGISTRATION_KEY);
                            sessionStorage.removeItem(process.env.REACT_APP_CHECKOUT_KEY);
                        } catch (e) {
                            // Don't interrupt the purchase flow, since the order already went through by this point
                            return null;
                        }
                    } else {
                        if (data.progress?.paymentSettled === true) {
                            const errorMessage = t`There was an issue with your order, and your card may have been charged. Please contact customer support before trying again.`;
                            setErrorMessage(errorMessage);
                            setStatus('Checkout');
                            GA4.exception(`Payment Error: ${errorMessage} - ${event.title}`);
                            return;
                        }

                        const errors = {
                            // Stripe Errors
                            incorrect_number: t`The card number entered is incorrect.`,
                            invalid_number: t`The card number entered is not a valid credit card number.`,
                            invalid_expiry_month: t`Your card's expiration month is invalid.`,
                            invalid_expiry_year: t`Your card's expiration year is invalid.`,
                            invalid_cvc: t`Your card's security code is invalid.`,
                            expired_card: t`Your card has expired.`,
                            incorrect_cvc: t`Your card's security code is incorrect.`,
                            incorrect_zip: t`Your card's zip code failed validation.`,
                            card_declined: t`Your card was declined.`,
                            missing: t`There is no card on a customer that is being charged.`,
                            processing_error: t`An error occurred while processing your credit card.`,
                            // FrontDoor+ Errors
                            bad_request: t`Please ensure your contact and payment information are correct.`,
                            sold_out: t`An item in your cart is sold out.`,
                            not_enough_stock: t`There are not enough tickets in stock to fulfill this order.`,
                            season_seats_not_configured: t`Season tickets are not available for purchase.`,
                            season_event_closed: t`Tickets are no longer available for this event.`,
                            assigned_seats_required: t`Please choose your seats before continuing.`,
                            seat_unavailable: t`One or more of your seats is no longer available. Please choose your seats again.`,
                            ticket_not_available: t`An item in your cart is not available for sale.`,
                            invalid_event: t`We are having issues accepting payments right now. Please try again later.`,
                            // Discount Code Errors
                            discount_invalid: t`The discount code is invalid. Please remove the code and try again.`,
                            discount_expired: t`The discount code has expired. Please remove the code and try again.`,
                            discount_limit_reached: t`The discount code has been used up. Please remove the code and try again.`,
                            discount_ticket_limit_reached: t`The discount code has been used up. Please remove the code and try again.`,
                            discount_customer_limit_reached: t`You have reached your limit for this discount code. Please remove the code and try again.`,
                            // Generic Error
                            default_error: t`Unable to process your payment.`,
                        };

                        const errorCode =
                            data.code === 'season_capacity_exceeded'
                                ? Number.isFinite(data.remaining) && data.remaining > 0
                                    ? 'not_enough_stock'
                                    : 'sold_out'
                                : data.code;
                        const errorMessage = errors[errorCode] || errors.default_error;

                        setErrorMessage(errorMessage);

                        if (data.code === 'seat_unavailable') {
                            // Nothing was booked: drop the stale seats and restart the seat session from live availability.
                            await releaseHoldToken();
                            setSeatsPicked([]);
                            Object.keys(sessionStorage).forEach((key) => {
                                if (key.startsWith('seatsio')) {
                                    sessionStorage.removeItem(key);
                                }
                            });
                        }

                        setStatus(
                            [
                                'season_seats_not_configured',
                                'season_event_closed',
                                'assigned_seats_required',
                                'seat_unavailable',
                            ].includes(data.code)
                                ? event.seatsEnabled
                                    ? 'SeatPicker'
                                    : 'Tickets'
                                : 'Checkout',
                        );

                        GA4.exception(`Payment Error: ${errorMessage} - ${event.title}`);
                    }
                })
                .catch(() => {
                    try {
                        setErrorMessage(t`There was a problem making your purchase`);

                        setStatus('Checkout');

                        GA4.exception(`Error - ${event.title}`, true);
                    } catch (e) {
                        return null;
                    }
                });
        } catch (err) {
            setErrorMessage(t`Something went wrong. Your purchase could not be completed.`);
            setStatus('Checkout');
        }
    }

    function isFormValid() {
        const {
            firstName,
            lastName,
            email,
            emailConfirm,
            address,
            city,
            country,
            province,
            postalCode,
            ccName,
            phone,
        } = customerForm;
        let newState = {};

        newState.firstName = isValid.name(firstName.value)
            ? { ...firstName, error: false, message: '' }
            : { ...firstName, error: true, message: t`Please enter your first name` };

        newState.lastName = isValid.name(lastName.value)
            ? { ...lastName, error: false, message: '' }
            : { ...lastName, error: true, message: t`Please enter your last name` };

        newState.email = isValid.email(email.value)
            ? { ...email, error: false, message: '' }
            : { ...email, error: true, message: t`Please enter a valid email address` };

        newState.emailConfirm = isValid.email(emailConfirm.value)
            ? { ...emailConfirm, error: false, message: '' }
            : { ...emailConfirm, error: true, message: t`Please enter a valid email address` };

        if (email.value.toLowerCase() !== emailConfirm.value.toLowerCase()) {
            newState.email = {
                ...email,
                error: true,
                message: t`The emails provided do not match`,
            };
            newState.emailConfirm = {
                ...emailConfirm,
                error: true,
                message: t`The emails provided do not match`,
            };
        }

        newState.address = isValid.address(address.value)
            ? { ...address, error: false, message: '' }
            : { ...address, error: true, message: t`Please enter your address` };

        newState.city = isValid.city(city.value)
            ? { ...city, error: false, message: '' }
            : { ...city, error: true, message: t`Please enter your city` };

        const countryProfile = getCountryProfile(country.value);
        newState.postalCode = isValid.postalCode(postalCode.value, country.value)
            ? { ...postalCode, error: false, message: '' }
            : {
                  ...postalCode,
                  error: true,
                  message: countryProfile.postalCode.errorMessage(),
              };

        if (countryProfile.region.mode === 'hidden' || !countryProfile.region.required) {
            newState.province = { ...province, error: false, message: '' };
        } else {
            newState.province = isValid.select(province.value)
                ? { ...province, error: false, message: '' }
                : {
                      ...province,
                      error: true,
                      message: countryProfile.region.errorMessage(),
                  };
        }

        // Validate ccName only if user buying paid products
        if (paidTransaction) {
            newState.ccName = isValid.name(ccName.value)
                ? { ...ccName, error: false, message: '' }
                : { ...ccName, error: true, message: t`Please enter your name` };
        }

        newState.phone = isValid.phone(phone.value, country.value)
            ? { ...phone, error: false, message: '' }
            : { ...phone, error: true, message: t`Please enter a valid phone number` };

        setCustomerForm({ ...customerForm, ...newState });

        // Return true if form is valid
        return !Object.keys(newState).some((key) => newState[key].error);
    }

    const handleDiscountSubmit = () => {
        if (discountFormState.status === 'loading') return;
        setErrorMessage('');
        discountDispatch({ type: 'setStatus', status: 'loading' });

        getDiscountByCode(discountFormState.code, event.eventId)
            .then((discount) => {
                // ** Discount Code Valid ** //
                if (discount.status === 'DISCOUNT_CODE_VALID') {
                    const { discountsApplied } = buildReceipt(
                        products,
                        bundles,
                        event,
                        discount,
                        seatsPicked,
                    );

                    if (discountsApplied) {
                        // Discount was applied to the order
                        if (discount.perItemDiscount) {
                            discountDispatch({
                                type: 'setStatus',
                                status: 'success',
                                message: t`Discount applied to eligible tickets in your order.`,
                            });
                        } else {
                            discountDispatch({
                                type: 'setStatus',
                                status: 'success',
                                message: t`Discount applied`,
                            });
                        }
                        onDiscountChange(discount);
                    } else {
                        // Discount was not applied to anything in the order
                        discountDispatch({
                            type: 'setStatus',
                            status: 'error',
                            message: t`There are no eligible tickets in your order`,
                        });
                    }
                }

                // ** Discount Code Invalid ** //
                else {
                    switch (discount.status) {
                        case 'DISCOUNT_CODE_NOT_FOUND':
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`Not a valid code`,
                            });
                            break;
                        case 'DISCOUNT_CODE_EXPIRED':
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`This code is expired`,
                            });
                            break;
                        case 'DISCOUNT_CODE_EXHAUSTED':
                        case 'DISCOUNT_CODE_TICKETS_EXHAUSTED':
                        case 'DISCOUNT_CODE_CUSTOMER_EXHAUSTED':
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`This code has been used up`,
                            });
                            break;
                        default:
                            discountDispatch({
                                type: 'setStatus',
                                status: 'error',
                                message: t`Unable to verify code`,
                            });
                            break;
                    }
                }
            })
            .catch(() => {
                discountDispatch({
                    type: 'setStatus',
                    status: 'error',
                    message: t`Unable to verify code`,
                });
            });
    };

    const handleDiscountChange = (value) => {
        setErrorMessage('');
        discountDispatch({ type: 'setCode', code: value });
    };

    const handleDiscountDelete = () => {
        setErrorMessage('');
        discountDispatch({ type: 'deleteCode' });
        onDiscountChange(null);
    };

    const disablePurchase = status === 'Submit' || discountFormState.status === 'loading';

    // Calculate step info for header
    const getStepInfo = () => {
        const steps = [];

        if (event.seatsEnabled) steps.push({ status: 'SeatPicker', label: t`Pick your seats` });
        if (products.some((p) => p.type === 1 || p.type === 2) || bundles.length > 0) {
            steps.push({
                status: 'Tickets',
                label: event.seatsEnabled ? t`Add extras (optional)` : t`Add tickets`,
            });
        }
        if (registrationRequired) steps.push({ status: 'Registration', label: t`More details` });
        steps.push({ status: 'Checkout', label: t`Payment` });

        const currentIndex = steps.findIndex((s) => s.status === status);
        const totalSteps = steps.length;
        const currentStep = currentIndex >= 0 ? currentIndex + 1 : null;
        const stepLabel = steps[currentIndex]?.label || '';

        return { steps, currentIndex, currentStep, totalSteps, stepLabel };
    };

    const stepInfo = getStepInfo();

    // Navigation handlers
    const handleBack = () => {
        const previousStep = stepInfo.steps[stepInfo.currentIndex - 1];

        if (previousStep.status === 'Registration' && registrationForm === null) {
            const attendeeForm = initializeAttendee(
                event.checkoutQuestionMode,
                products,
                bundles,
                seatsPicked,
                registrationQuestions.custom,
                registrationQuestions.required,
            );

            setRegistrationForm(attendeeForm);
        }

        // An error belongs to the attempt that raised it; don't carry it onto another step.
        setErrorMessage('');
        setStatus(previousStep.status);
    };

    const handleContinue = () => {
        const currentStep = stepInfo.steps[stepInfo.currentIndex];
        const nextStep = stepInfo.steps[stepInfo.currentIndex + 1];

        if (nextStep.status === 'Registration' && registrationForm === null) {
            const attendeeForm = initializeAttendee(
                event.checkoutQuestionMode,
                products,
                bundles,
                seatsPicked,
                registrationQuestions.custom,
                registrationQuestions.required,
            );

            setRegistrationForm(attendeeForm);
        }

        if (currentStep.status === 'Registration') {
            const { error, form } = validateForm(registrationForm);

            if (error) {
                setRegistrationForm(form);
                createNotification(t`Please ensure the form is filled in correctly`, {
                    variant: 'warning',
                });
                return; // Don't continue if Registration form in invalid
            }
        }

        setErrorMessage('');
        setStatus(nextStep.status);
    };

    // Control bar visibility logic
    const showControlBar = ['SeatPicker', 'Tickets', 'Registration', 'Checkout'].includes(status);
    const showReviewCart = status === 'SeatPicker';
    const showBackButton =
        (status === 'Tickets' && event.seatsEnabled) ||
        status === 'Registration' ||
        status === 'Checkout';
    const showContinueButton = status === 'Tickets' || status === 'Registration';

    return (
        <CheckoutContext.Provider
            value={{
                setSeatsPicked,
                seatsPicked,
                handleDiscountChange,
                handleDiscountDelete,
                disablePurchase,
                handleDiscountSubmit,
                errorMessage,
                handleFormSubmit,
                paymentForm,
                handleFormChange,
                handleAddressAutocomplete,
                onCancel,
                paidTransaction,
                showDiscountForm,
                isFormValid,
                status,
                contentRef,
                setStatus,
                registrationForm,
                setRegistrationForm,
                event,
                customerForm,
                discount,
                discountFormState,
                bundles,
                products,
                receiptData,
                // Overriding add/remove product/bundle methods
                handleAddProduct: handleAddProductOverride,
                handleRemoveProduct: handleRemoveProductOverride,
                handleAddBundle: handleAddBundleOverride,
                handleRemoveBundle: handleRemoveBundleOverride,
                // Step info for header
                stepInfo,
                // Navigation handlers
                handleBack,
                handleContinue,
                // Control bar visibility
                showControlBar,
                showReviewCart,
                showBackButton,
                showContinueButton,
                // Session timer
                formattedTimer,
                holdToken,
                handleSessionInitialized,
                handleSessionExpired,
                releaseHoldToken,
                // Chart control
                setChartRef,
                clearSelection,
                deselectSeat,
                chartKey,
                // Session alert
                sessionAlert,
                closeSessionAlert,
                // Session expired dialog
                sessionExpiredDialogOpen,
                handleSessionExpiredDialogClose,
                chartSelectionErrors,
                setChartSelectionErrors,
                // Robin SMS opt-in (customer consent; stored on order and in checkout session)
                robinOptIn,
                setRobinOptIn,
            }}
        >
            {children}
        </CheckoutContext.Provider>
    );
};
